import * as React from 'react';
import {
  CacheLookupPolicy,
  InteractionRequiredAuthError,
  InteractionStatus
} from '@azure/msal-browser';
import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react';

import { setAuthTokenProvider, setManagerGraphTokenProvider, setGroupGraphTokenProvider } from '@cdf/api-client-react';
import {
  buildGraphTokenRequest,
  type GraphAccess,
  buildLoginRequest,
  buildTokenRequest,
  getApiScopes,
  getPostLogoutRedirectUri,
  isAuthConfigured
} from '@/lib/auth-config';
import { isInIframe, openTopLevelAuth } from '@/lib/auth-navigation';
import { setGraphTokenProvider } from '@/lib/graph-client';
import { useAuthMode } from '@/components/auth/auth-mode';
import { acquireApiTokenSilently } from '@/lib/api-token-recovery';
import { createGroupAccessController } from '@/lib/group-access-recovery';

export interface AuthContextValue {
  enabled: boolean;
  ready: boolean;
  isAuthenticated: boolean;
  account: { name: string; email: string | null } | null;
  /** Stable MSAL identity key for partitioning delegated Graph caches. */
  accountKey: string | null;
  login: () => void;
  reconnect: () => void;
  interactionError: string | null;
  tokenError: boolean;
  logout: () => void;
  /** Delegated custom API token — never ID or Graph tokens. */
  getToken: () => Promise<string | null>;
  /**
   * Delegated Microsoft Graph access token (Place.Read.All, Calendars.Read).
   * Silent-first; interactive consent uses top-level handoff inside iframes.
   */
  getGraphToken: (access: GraphAccess) => Promise<string | null>;
  /** Explicit interactive consent for one Graph permission (user click). */
  requestGraphConsent: (access: GraphAccess) => void;
}

const DISABLED_AUTH: AuthContextValue = {
  enabled: false,
  ready: true,
  isAuthenticated: false,
  account: null,
  accountKey: null,
  login: () => undefined,
  reconnect: () => undefined,
  interactionError: null,
  tokenError: false,
  logout: () => undefined,
  getToken: async () => null,
  getGraphToken: async () => null,
  requestGraphConsent: () => undefined
};

const AuthContext = React.createContext<AuthContextValue>(DISABLED_AUTH);

function useMsalAuthValue(): AuthContextValue {
  const { instance, inProgress } = useMsal();
  // Reactive MSAL hooks only during render — never getActiveAccount()/getAllAccounts().
  const isAuthenticated = useIsAuthenticated();
  const activeAccount = useAccount();
  const ready = inProgress === InteractionStatus.None;
  const iframe = React.useMemo(() => isInIframe(), []);
  const interactionPending = React.useRef(false);
  const groupController = React.useRef<ReturnType<typeof createGroupAccessController> | null>(null);
  const readyRef = React.useRef(ready);
  React.useLayoutEffect(() => { readyRef.current = ready; }, [ready]);
  const [interactionError, setInteractionError] = React.useState<string | null>(null);
  const [tokenError, setTokenError] = React.useState(false);

  const login = React.useCallback(() => {
    if (inProgress !== InteractionStatus.None || interactionPending.current) return;
    interactionPending.current = true;
    setInteractionError(null);
    if (iframe) {
      openTopLevelAuth('login');
      interactionPending.current = false;
      return;
    }
    void instance.loginRedirect(buildLoginRequest(window.location.href)).catch(() => {
      interactionPending.current = false;
      setInteractionError('Sign-in could not start. Please try again.');
    });
  }, [instance, inProgress, iframe]);

  const logout = React.useCallback(() => {
    if (inProgress !== InteractionStatus.None || interactionPending.current) return;
    interactionPending.current = true;
    setGroupGraphTokenProvider(null);
    groupController.current?.dispose();

    if (iframe) {
      interactionPending.current = false;
      openTopLevelAuth('logout');
      return;
    }

    // Resolve account at call time — avoids stale event-listener closures.
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? undefined;
    void instance
      .logoutRedirect({
        account,
        postLogoutRedirectUri: getPostLogoutRedirectUri()
      })
      .catch(() => {
        interactionPending.current = false;
        setInteractionError('Sign-out could not start. Please try again.');
      });
  }, [instance, inProgress, iframe]);

  const getToken = React.useCallback(async (): Promise<string | null> => {
    if (inProgress !== InteractionStatus.None) return null;

    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) return null;

    const apiScopes = getApiScopes();
    if (!apiScopes) { setTokenError(true); return null; }
    const request = {
      ...buildTokenRequest(account),
      cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken
    };

    return acquireApiTokenSilently(() => instance.acquireTokenSilent(request), setTokenError);
  }, [instance, inProgress]);

  const reconnect = React.useCallback(() => {
    if (inProgress !== InteractionStatus.None || interactionPending.current) return;
    if (!getApiScopes()) {
      setInteractionError('Application access is not configured. Contact your administrator.');
      return;
    }
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) { login(); return; }
    interactionPending.current = true;
    setInteractionError(null);
    if (iframe) {
      openTopLevelAuth('acquireToken');
      interactionPending.current = false;
      return;
    }
    void instance.acquireTokenRedirect(buildTokenRequest(account, window.location.href)).catch(() => {
      interactionPending.current = false;
      setInteractionError('Application access could not be renewed. Please try again or contact your administrator.');
    });
  }, [instance, inProgress, iframe, login]);

  const requestGraphConsent = React.useCallback((access: GraphAccess) => {
    if (inProgress !== InteractionStatus.None || interactionPending.current) return;
    interactionPending.current = true;

    if (iframe) {
      interactionPending.current = false;
      setInteractionError(null);
      const opened = openTopLevelAuth(access === 'places' ? 'acquireGraphPlaces'
        : access === 'presence' ? 'acquireGraphPresence'
        : access === 'directory' ? 'acquireGraphDirectory'
        : access === 'managerReports' ? 'acquireGraphManagerReports'
        : access === 'groups' ? 'acquireGraphGroups' : 'acquireGraphCalendar');
      if (!opened) setInteractionError('Allow a new tab for Microsoft consent, then try again.');
      return;
    }

    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) {
      void instance.loginRedirect(buildLoginRequest(window.location.href)).catch(() => {
        interactionPending.current = false;
        setInteractionError('Sign-in could not start. Please try again.');
      });
      return;
    }

    void instance
      .acquireTokenRedirect(buildGraphTokenRequest(account, window.location.href, access))
      .catch(() => {
        interactionPending.current = false;
        setInteractionError('Microsoft Graph consent could not start. Please try again.');
      });
  }, [instance, inProgress, iframe]);

  const getGraphToken = React.useCallback(async (access: GraphAccess): Promise<string | null> => {
    if (inProgress !== InteractionStatus.None) return null;
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) return null;

    const request = {
      ...buildGraphTokenRequest(account, undefined, access),
      cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken
    };

    try {
      const result = await instance.acquireTokenSilent(request);
      const current = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      if (current?.homeAccountId !== account.homeAccountId || current.tenantId !== account.tenantId) return null;
      return result.accessToken || null;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Do not auto-redirect from a background silent attempt — the page
        // shows a consent CTA; requestGraphConsent handles the user gesture.
        return null;
      }
      console.error('Microsoft access could not be acquired.');
      return null;
    }
  }, [instance, inProgress]);

  // Register before descendant passive query effects, without side effects
  // during render. Callback updates remain synchronized with MSAL readiness.
  React.useLayoutEffect(() => {
    setAuthTokenProvider(getToken);
    setGraphTokenProvider(getGraphToken);
    setManagerGraphTokenProvider(() => getGraphToken('managerReports'));
    return () => {
      setAuthTokenProvider(null);
      setGraphTokenProvider(null);
      setManagerGraphTokenProvider(null);
    };
  }, [getToken, getGraphToken, activeAccount?.homeAccountId, activeAccount?.tenantId]);

  React.useLayoutEffect(() => {
    const identity = () => {
      const current = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      return current ? [current.homeAccountId, current.tenantId, current.localAccountId].join(':') : null;
    };
    const controller = createGroupAccessController(identity, async () => {
      const current = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      if (!current) return null;
      const result = await instance.acquireTokenSilent({
        ...buildGraphTokenRequest(current, undefined, 'groups'),
        cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken
      });
      return result.accessToken || null;
    });
    groupController.current = controller;
    setGroupGraphTokenProvider(() => readyRef.current ? controller.token() : Promise.resolve(null), identity);
    return () => {
      setGroupGraphTokenProvider(null);
      controller.dispose();
      if (groupController.current === controller) groupController.current = null;
    };
  }, [instance, activeAccount?.homeAccountId, activeAccount?.tenantId, activeAccount?.localAccountId]);

  const account = activeAccount
    ? {
        name: activeAccount.name ?? activeAccount.username,
        email: activeAccount.username || null
      }
    : null;

  return {
    enabled: true,
    ready,
    isAuthenticated,
    account,
    accountKey: activeAccount ? [activeAccount.homeAccountId, activeAccount.tenantId, activeAccount.localAccountId].join(':') : null,
    login,
    reconnect,
    interactionError,
    tokenError,
    logout,
    getToken,
    getGraphToken,
    requestGraphConsent
  };
}

/** Provides MSAL-backed auth when the root bootstrap mounted MsalProvider. */
function MsalAuthProvider({ children }: { children: React.ReactNode }) {
  const value = useMsalAuthValue();
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Auth context owner.
 * When authentication is disabled, children receive an honest dormant provider.
 * When enabled, must be rendered under MsalProvider (see main.tsx).
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { enabled } = useAuthMode();
  if (!enabled) {
    return <AuthContext.Provider value={DISABLED_AUTH}>{children}</AuthContext.Provider>;
  }
  // Soft guard: configuration missing still renders dormant (main may have skipped MSAL).
  if (!isAuthConfigured()) {
    return <AuthContext.Provider value={DISABLED_AUTH}>{children}</AuthContext.Provider>;
  }
  return <MsalAuthProvider>{children}</MsalAuthProvider>;
}

/**
 * User-triggered auth operations only.
 * No URL/query effects — AuthLifecycle owns those side effects.
 */
export function useAuth(): AuthContextValue {
  return React.useContext(AuthContext);
}
